import type { TestFooterFontSchemaType, TestFooterSchemaType } from '@vedalib/editor/lib/brand'
import React, { useEffect } from 'react'

import { useLiveFeedback } from 'components/Editor/EditorElements/courseKit/CourseKitUtils'
import { useElementRootStyles } from 'components/Editor/EditorElements/hooks/useElementRootStyles'
import Progress from 'components/uiKit/Progress'
import type { ElementFontCss, ElementStyleCss } from 'services/Branding/types'
import { useProjectT } from 'services/ProjectLabels'
import { useProjectContext } from 'services/Store/Project/hooks'
import { getSection } from 'services/Store/Project/selectors'
import { getBlock } from 'services/Store/Project/selectors/data/block'

import BlockTestTimer from './BlockTestTimer'
import SectionTestTimer from './SectionTestTimer'
import * as s from './TestProgressFooter.module.scss'
import { formatDuration } from './useTestTimer'

interface ITestProgressFooterProps {
  fonts: ElementFontCss<TestFooterFontSchemaType>
  styles: ElementStyleCss<TestFooterSchemaType>
  isProgressShown?: boolean | null
  isSectionTime?: number | false | null
  isBlockTime?: boolean | 0 | null
  testLimit?: boolean | null
  testProgress?: number
  totalQuestionsDone?: number
  totalQuestions?: number
  currentTry?: number
  testRetryCount?: number | null
}

const TestProgressFooter: React.FC<ITestProgressFooterProps> = ({
  styles,
  fonts,
  isProgressShown,
  testProgress,
  totalQuestions,
  totalQuestionsDone,
  isSectionTime,
  isBlockTime,
  testLimit,
  currentTry,
  testRetryCount,
}) => {
  const pt = useProjectT()
  const rootStyles = useElementRootStyles(styles.root, styles.panel, fonts?.base)
  const blockTime = useProjectContext(getBlock)?.test?.time || 0
  const sectionTime = useProjectContext(getSection)?.test?.time || 0
  const announce = useLiveFeedback('polite')

  useEffect(() => {
    if (
      isProgressShown &&
      testProgress !== undefined &&
      totalQuestionsDone !== undefined &&
      totalQuestions !== undefined
    ) {
      const progressText = `Question ${totalQuestionsDone} of ${totalQuestions}.`
      const timeMessage =
        isSectionTime && totalQuestionsDone === 0
          ? `Time for the section: ${formatDuration(sectionTime * 1000)}.`
          : isBlockTime
            ? `Time for the block: ${formatDuration(blockTime * 1000)}.`
            : ''
      announce(`${progressText} ${timeMessage}`)
    }
  }, [
    isProgressShown,
    testProgress,
    totalQuestionsDone,
    totalQuestions,
    announce,
    isSectionTime,
    sectionTime,
    isBlockTime,
    blockTime,
  ])

  return (
    <div className={s.root} style={rootStyles}>
      <div className={s.progress}>
        {isProgressShown && (
          <>
            <Progress
              ariaLabelledBy='testFooter-label'
              customStyles={{ stroke: 'var(--brand-focusedColor)' }}
              name='testFooter'
              percent={testProgress || 0}
            />
            <div className={s.progressLabel} id='testFooter-label'>
              <span>
                {totalQuestionsDone} / {totalQuestions}
              </span>
            </div>
          </>
        )}
      </div>
      {isSectionTime && <SectionTestTimer totalQuestionsDone={totalQuestionsDone} />}
      {!isSectionTime && isBlockTime && <BlockTestTimer />}
      {testLimit && (
        <div className={s.tryCount}>
          <span>
            {pt('footer.try')} : {currentTry ?? 1} {pt('footer.from')} {testRetryCount}
          </span>
        </div>
      )}
    </div>
  )
}

export default TestProgressFooter
